// Briefing forecast rows (pure, unit-tested): the segments the predicted range will cover, in display form.
import type { Route, AirSegment, SeaSegment } from '../types';
import { AIR_LAYERS_FT } from '../types';
import { isAirRoute } from '../sim/route';

export interface ForecastRow {
  /** 1-based segment number. */
  no: number;
  fromNm: number;
  toNm: number;
  /** Aircraft: along-track wind per AIR_LAYERS_FT layer, signed and rounded (kt). */
  windKt?: number[];
  /** Vessels: along-track current, signed, one decimal (kn). */
  currentKn?: number;
  seaState?: number;
  weather: boolean;
}

export const MAX_FORECAST_ROWS = 40;

/** Column labels for the aircraft wind layers ("Sfc", "10k", … ft). */
export const AIR_LAYER_LABELS: readonly string[] = AIR_LAYERS_FT.map((ft) => (ft === 0 ? 'Sfc' : `${ft / 1000}k ft`));

/**
 * Segments from the start up to and including the one that contains `rangeNm` (at least one), capped at
 * MAX_FORECAST_ROWS. `truncated` says whether the cap cut rows off.
 */
export function forecastRows(route: Route, rangeNm: number): { rows: ForecastRow[]; truncated: boolean } {
  const rows: ForecastRow[] = [];
  const segs = route.segments;
  let truncated = false;
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    if (i > 0 && seg.fromNm >= rangeNm) break;
    if (rows.length >= MAX_FORECAST_ROWS) { truncated = true; break; }
    const base: ForecastRow = { no: i + 1, fromNm: seg.fromNm, toNm: seg.toNm, weather: !!seg.weatherCell };
    if (isAirRoute(route)) {
      const a = seg as AirSegment;
      rows.push({ ...base, windKt: a.windKtByLayer.map((w) => Math.round(w)) });
    } else {
      const s = seg as SeaSegment;
      rows.push({ ...base, currentKn: Math.round(s.currentKn * 10) / 10, seaState: s.seaState });
    }
  }
  return { rows, truncated };
}

/** "+12" / "−8" / "0" — signed integer with a true minus sign. */
export function signed(v: number, decimals = 0): string {
  const r = decimals > 0 ? v.toFixed(decimals) : String(Math.round(v));
  if (v > 0) return `+${r}`;
  if (v < 0) return `−${r.replace('-', '')}`;
  return decimals > 0 ? (0).toFixed(decimals) : '0';
}
